import React, { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import AppButton from "../common/AppButton";
import { OverlayPortal } from "../overlay-portal/OverlayPortal";
import styles from "./Modal.module.css";

const PRESET_WIDTHS = {
  "max-w-sm": "24rem", "max-w-md": "28rem", "max-w-lg": "32rem",
  "max-w-xl": "36rem", "max-w-2xl": "42rem", "max-w-3xl": "48rem",
  "max-w-4xl": "56rem",
};
const SIZE_WIDTHS = { xs: "320px", sm: "400px", md: "500px", lg: "700px", xl: "900px", fullscreen: "100%" };

const getButtonVariant = (button) => {
  if (button.variant) return button.variant;
  const className = String(button.className || "");
  if (/\b(?:btn-danger|bg-red-\d+|text-red-\d+)\b/.test(className)) return "danger";
  if (/\b(?:btn-success|bg-green-\d+|text-green-\d+)\b/.test(className)) return "success";
  if (/\b(?:btn-secondary|btn-outline-secondary)\b/.test(className) || /^(cancel|close|back|dismiss)\b/i.test(button.label)) return "secondary";
  return "primary";
};

const Modal = ({
  show, title, subtitle, children, onClose,
  showCloseButton = true, showCrossButton = true,
  width = "auto", buttons = [], footer, isLock = true,
  maxHeight = "75vh", alignment = "center", size = "md",
  mobilePresentation = "sheet", className = "",
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const backdropRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!show) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const viewport = window.visualViewport;
    const updateViewport = () => {
      if (!viewport || !backdropRef.current) return;
      backdropRef.current.style.setProperty("--modal-viewport-height", `${viewport.height}px`);
      backdropRef.current.style.setProperty("--modal-viewport-top", `${viewport.offsetTop}px`);
    };
    updateViewport();
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    return () => {
      document.body.style.overflow = previousOverflow;
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [show]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (!isLock) onCloseRef.current?.();
    }
    if (event.key !== "Tab") return;
    const focusable = [...dialogRef.current.querySelectorAll(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
    )].filter(element => !element.closest("[hidden]"));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
    } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!show) return null;
  const modalWidth = typeof width === "number" ? `${width}px`
    : width === "auto" ? SIZE_WIDTHS[size] : PRESET_WIDTHS[width] || width;
  const hasFooter = footer != null || buttons.length > 0 || showCloseButton;

  return (
    <OverlayPortal>
      <div
        ref={backdropRef}
        className={styles.backdrop}
        data-mobile-presentation={mobilePresentation}
        data-alignment={alignment}
        data-fullscreen={size === "fullscreen" ? "true" : undefined}
        style={{ "--modal-width": modalWidth, "--modal-body-max-height": maxHeight }}
        onKeyDown={handleKeyDown}
        onClick={event => {
          if (event.target === event.currentTarget && !isLock) onClose?.();
        }}
      >
        <section
          ref={dialogRef}
          className={`${styles.panel} ${className}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : "Dialog"}
          tabIndex={-1}
        >
          <div className={styles.sheetHandle} aria-hidden="true" />
          {(title || subtitle || showCrossButton) && (
            <header className={styles.header}>
              <div className={styles.heading}>
                {title && <h2 id={titleId} className={styles.title}>{title}</h2>}
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              </div>
              {showCrossButton && (
                <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close dialog">
                  <span aria-hidden="true">&times;</span>
                </button>
              )}
            </header>
          )}
          <div className={styles.body} data-has-footer={hasFooter}>{children}</div>
          {hasFooter && (
            <footer className={styles.footer}>
              {footer != null ? footer : <>
                {buttons.map((button, index) => (
                  <AppButton key={index} type={button.type || "button"} form={button.form}
                    onClick={button.onClick} disabled={button.disabled}
                    variant={getButtonVariant(button)} className={styles.actionButton}>
                    {button.label}
                  </AppButton>
                ))}
                {showCloseButton && (
                  <AppButton onClick={onClose} variant="secondary" className={styles.actionButton}>Close</AppButton>
                )}
              </>}
            </footer>
          )}
        </section>
      </div>
    </OverlayPortal>
  );
};

Modal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func,
  showCloseButton: PropTypes.bool,
  showCrossButton: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "fullscreen"]),
  buttons: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node.isRequired, onClick: PropTypes.func,
    disabled: PropTypes.bool, className: PropTypes.string,
    variant: PropTypes.oneOf(["primary", "secondary", "success", "danger"]),
    type: PropTypes.oneOf(["button", "submit", "reset"]), form: PropTypes.string,
  })),
  footer: PropTypes.node,
  isLock: PropTypes.bool,
  maxHeight: PropTypes.string,
  alignment: PropTypes.oneOf(["top", "center", "bottom"]),
  mobilePresentation: PropTypes.oneOf(["sheet", "modal"]),
  className: PropTypes.string,
};

export default Modal;
